import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import styles from '../BattleViewer2D.module.css';
import { parseCombatTag, isRewardLine } from '../../../../utils/combatLogUtils';

import {
  BattleData, ParticipantSummary, Sprite, AnimEvent,
  DamageFloat, Particle, CameraState, LogRound, LogEntry,
  CANVAS_W, CANVAS_H, ARENA_INSET, CIRCLE_RADIUS, HP_BAR_H,
  CHARGE_DURATION, BASE_EVENT_INTERVAL, IDLE_SPEED,
  TEAM_COLORS,
} from './types';
import { teamDisplayName, lerp, clamp } from './utils';
import { initAmbientParticles, updateParticles, drawParticles } from './particles';
import {
  drawArena, drawSprite, drawDamageFloats, drawTeamHPBars,
  drawRoundTransition, drawRoundBadge,
  applyCameraTransform, resetCameraTransform,
} from './renderers';
import { buildAnimQueue, initSprites } from './animBuilder';
import { applyEvent, ApplyEventContext } from './eventApplicator';

// ─── Component ──────────────────────────────────────────────────────────────

interface BattleViewer2DProps {
  initialBattleData?: BattleData;
  onComplete?: () => void;
  /** The current user's mercenary ID. When set, exact HP is only shown for that combatant. */
  myMercenaryId?: number;
}

const BattleViewer2D: React.FC<BattleViewer2DProps> = ({ initialBattleData, onComplete, myMercenaryId }) => {
  const [battleIdInput, setBattleIdInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [battleData, setBattleData] = useState<BattleData | null>(initialBattleData ?? null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const [logVersion, setLogVersion] = useState(0);
  const [currentRound, setCurrentRound] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const [expandedRoundIds, setExpandedRoundIds] = useState<Set<number>>(new Set());
  const [hoveredSprite, setHoveredSprite] = useState<{ key: string; x: number; y: number } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const spritesRef = useRef<Map<string, Sprite>>(new Map());
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const animQueueRef = useRef<AnimEvent[]>([]);
  const logRoundsRef = useRef<LogRound[]>([]);
  const pendingEntriesRef = useRef<LogEntry[]>([]);
  const currentRoundRef = useRef<LogRound | null>(null);
  const entryIdRef = useRef(0);
  const dmgFloatsRef = useRef<DamageFloat[]>([]);
  const dmgFloatIdRef = useRef(0);
  const particlesRef = useRef<Particle[]>([]);
  const queueIndexRef = useRef(0);
  const lastEventTimeRef = useRef(0);
  const speedRef = useRef(1);
  const playingRef = useRef(false);
  const allDoneRef = useRef(false);
  const rafIdRef = useRef(0);
  const timeRef = useRef(0);
  const currentRoundNumRef = useRef(0);

  const cameraRef = useRef<CameraState>({
    offsetX: 0, offsetY: 0,
    zoom: 1, targetZoom: 1,
    targetOffsetX: 0, targetOffsetY: 0,
    shakeX: 0, shakeY: 0, shakeTimer: 0,
  });

  const roundTransitionRef = useRef({ round: 0, alpha: 0, timer: 0 });
  const narrationRef = useRef({ text: '', displayedChars: 0, timer: 0, visible: false });
  const slowMoRef = useRef({ active: false, timer: 0, factor: 1 });
  const comboMapRef = useRef<Record<string, { count: number; lastActor: string }>>({});
  const themeBorderColorRef = useRef('#5c4a2e');
  const themeAccentRef = useRef<string>('#6ea8d0');
  const svgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const tintedSvgCacheRef = useRef<Map<string, HTMLCanvasElement>>(new Map());

  // Read theme accent color from CSS variables
  useEffect(() => {
    const style = getComputedStyle(document.documentElement);
    const gold = style.getPropertyValue('--color-gold').trim();
    if (gold) themeBorderColorRef.current = gold;
    const accent = style.getPropertyValue('--color-link-primary').trim();
    if (accent) {
      themeAccentRef.current = accent;
      tintedSvgCacheRef.current.clear(); // invalidate tinted cache on theme change
    }
  }, []);

  // Load SVG icons for weapons and status indicators
  useEffect(() => {
    const SVG_NAMES = ['axe', 'sword', 'mace', 'staff', 'dagger', 'chain', 'shield', 'skull', 'flag', 'zzz'];
    const cache = svgCacheRef.current;
    SVG_NAMES.forEach(name => {
      if (!cache.has(name)) {
        const img = new Image();
        img.src = `/img/battle_icons/${name}.svg`;
        img.onload = () => cache.set(name, img);
      }
    });
  }, []);

  const resetLogRefs = useCallback(() => {
    logRoundsRef.current = [];
    pendingEntriesRef.current = [];
    currentRoundRef.current = null;
    entryIdRef.current = 0;
    dmgFloatsRef.current = [];
    particlesRef.current = initAmbientParticles();
    comboMapRef.current = {};
    currentRoundNumRef.current = 0;
    setExpandedRoundIds(new Set());
  }, []);

  const toggleRound = useCallback((roundId: number) => {
    setExpandedRoundIds(prev => {
      const next = new Set(prev);
      if (next.has(roundId)) next.delete(roundId);
      else next.add(roundId);
      return next;
    });
  }, []);

  useEffect(() => { speedRef.current = speedMultiplier; }, [speedMultiplier]);
  useEffect(() => { playingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { allDoneRef.current = allDone; }, [allDone]);

  useEffect(() => {
    axios.get('/api/user/settings', { withCredentials: true })
      .then(res => { if (res.data.battleViewSpeed) setSpeedMultiplier(Number(res.data.battleViewSpeed)); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialBattleData) return;
    const timer = setTimeout(() => setIsPlaying(true), 800);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allDone && onComplete) onComplete();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  // Wrapped setCurrentRound that also updates the ref
  const handleSetCurrentRound = useCallback((r: number) => {
    currentRoundNumRef.current = r;
    setCurrentRound(r);
  }, []);

  const buildEventCtx = useCallback((): ApplyEventContext => ({
    sprites: spritesRef.current,
    dmgFloats: dmgFloatsRef.current,
    dmgFloatId: dmgFloatIdRef,
    particles: particlesRef.current,
    camera: cameraRef.current,
    logRounds: logRoundsRef.current,
    pendingEntries: pendingEntriesRef.current,
    currentRound: currentRoundRef,
    entryId: entryIdRef,
    setCurrentRound: handleSetCurrentRound,
    setLogVersion,
    roundTransition: roundTransitionRef.current,
    narration: narrationRef.current,
    slowMo: slowMoRef.current,
    comboMap: comboMapRef.current,
  }), [handleSetCurrentRound]);

  const drawStaticFrame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    drawArena(ctx, 0, themeBorderColorRef.current);
    spritesRef.current.forEach(s => drawSprite(ctx, s, imageCacheRef.current, svgCacheRef.current, tintedSvgCacheRef.current, themeAccentRef.current));
    // HUD
    drawRoundBadge(ctx, currentRoundNumRef.current);
    drawTeamHPBars(ctx, spritesRef.current);
  }, []);

  const loadBattle = useCallback(async () => {
    const id = parseInt(battleIdInput, 10);
    if (!id || isNaN(id)) { setErrorMsg('Please enter a valid battle ID.'); return; }
    setLoading(true); setErrorMsg(null); setBattleData(null);
    setIsPlaying(false); setAllDone(false); setCurrentRound(0);
    resetLogRefs(); setLogVersion(v => v + 1);
    try {
      const res = await axios.get(`/api/battle/result/${id}`);
      setBattleData(res.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setErrorMsg(err?.response?.data?.message ?? 'Failed to load battle.');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleIdInput]);

  useEffect(() => {
    if (!battleData) return;
    spritesRef.current = initSprites(battleData.reportData.participantSummaries);
    animQueueRef.current = buildAnimQueue(
      battleData.reportData.combatLog,
      battleData.reportData.participantSummaries,
    );
    queueIndexRef.current = 0;
    lastEventTimeRef.current = 0;
    resetLogRefs();
    allDoneRef.current = false;
    setCurrentRound(0); setAllDone(false);
    setLogVersion(v => v + 1);
    timeRef.current = 0;

    const cam = cameraRef.current;
    cam.zoom = 1; cam.targetZoom = 1;
    cam.offsetX = 0; cam.offsetY = 0;
    cam.targetOffsetX = 0; cam.targetOffsetY = 0;
    cam.shakeX = 0; cam.shakeY = 0; cam.shakeTimer = 0;

    imageCacheRef.current.clear();
    Object.values(battleData.reportData.participantSummaries).forEach(p => {
      const { race, sex, avatar: num } = p;
      if (!race || !sex || !num) return;
      const key = `${race.toLowerCase()}.${sex.toLowerCase()}.${num}`;
      if (!imageCacheRef.current.has(key)) {
        const img = new Image(); img.src = `/img/avatars/${key}.png`;
        imageCacheRef.current.set(key, img);
      }
    });

    requestAnimationFrame(drawStaticFrame);
  }, [battleData, drawStaticFrame, resetLogRefs]);

  const handleSetSpeed = useCallback((s: number) => {
    setSpeedMultiplier(s);
    axios.put('/api/user/settings', { battleViewSpeed: s }, { withCredentials: true }).catch(() => {});
  }, []);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    let closestKey: string | null = null;
    let closestDist = CIRCLE_RADIUS * 1.5;
    spritesRef.current.forEach(s => {
      if (s.state === 'dead') return;
      const dx = s.x - mx;
      const dy = s.y - my;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < closestDist) {
        closestKey = s.key;
        closestDist = dist;
      }
    });

    if (closestKey) {
      setHoveredSprite({ key: closestKey, x: e.clientX - rect.left + 14, y: e.clientY - rect.top - 10 });
    } else {
      setHoveredSprite(null);
    }
  }, []);

  const handleCanvasMouseLeave = useCallback(() => setHoveredSprite(null), []);

  const skipRound = useCallback(() => {
    if (!battleData) return;
    const queue = animQueueRef.current;
    const startIdx = queueIndexRef.current;
    let targetIdx = queue.length;
    for (let i = startIdx; i < queue.length; i++) {
      if (i > startIdx && queue[i].eventType === 'round_header') { targetIdx = i; break; }
    }
    const evtCtx = buildEventCtx();
    while (queueIndexRef.current < targetIdx) {
      applyEvent(queue[queueIndexRef.current], evtCtx);
      queueIndexRef.current++;
    }
    lastEventTimeRef.current = 0;
    requestAnimationFrame(drawStaticFrame);
  }, [battleData, buildEventCtx, drawStaticFrame]);

  const skipToEnd = useCallback(() => {
    if (!battleData) return;
    const queue = animQueueRef.current;
    const evtCtx = buildEventCtx();
    while (queueIndexRef.current < queue.length) {
      applyEvent(queue[queueIndexRef.current], evtCtx);
      queueIndexRef.current++;
    }
    allDoneRef.current = true;
    setAllDone(true); setIsPlaying(false);
    requestAnimationFrame(drawStaticFrame);
  }, [battleData, buildEventCtx, drawStaticFrame]);

  const restart = useCallback(() => {
    if (!battleData) return;
    cancelAnimationFrame(rafIdRef.current);
    spritesRef.current = initSprites(battleData.reportData.participantSummaries);
    animQueueRef.current = buildAnimQueue(
      battleData.reportData.combatLog,
      battleData.reportData.participantSummaries,
    );
    queueIndexRef.current = 0;
    lastEventTimeRef.current = 0;
    resetLogRefs();
    allDoneRef.current = false;
    setAllDone(false); setCurrentRound(0); setIsPlaying(false);
    setLogVersion(v => v + 1);
    timeRef.current = 0;
    const cam = cameraRef.current;
    cam.zoom = 1; cam.targetZoom = 1;
    cam.offsetX = 0; cam.offsetY = 0;
    cam.targetOffsetX = 0; cam.targetOffsetY = 0;
    requestAnimationFrame(drawStaticFrame);
  }, [battleData, drawStaticFrame, resetLogRefs]);

  // ── RAF animation loop ────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying || !battleData) return;
    let prevTime = performance.now();

    const tick = (now: number) => {
      if (!playingRef.current) return;

      try {
        let dt = Math.min((now - prevTime) / 1000, 0.1);
        prevTime = now;
        const canvas = canvasRef.current;
        if (!canvas) { rafIdRef.current = requestAnimationFrame(tick); return; }
        const ctx = canvas.getContext('2d');
        if (!ctx) { rafIdRef.current = requestAnimationFrame(tick); return; }

        // Slow-mo
        const sm = slowMoRef.current;
        if (sm.active) {
          sm.timer -= dt;
          if (sm.timer <= 0) {
            sm.active = false; sm.factor = 1;
            cameraRef.current.targetZoom = 1;
            cameraRef.current.targetOffsetX = 0;
            cameraRef.current.targetOffsetY = 0;
          }
          dt *= sm.factor;
        }

        const speed = speedRef.current;
        const interval = BASE_EVENT_INTERVAL / speed;
        timeRef.current += dt;

        // Fire next event
        if (queueIndexRef.current < animQueueRef.current.length) {
          const elapsed = lastEventTimeRef.current === 0
            ? interval
            : now - lastEventTimeRef.current;
          if (elapsed >= interval) {
            const evtCtx = buildEventCtx();
            applyEvent(animQueueRef.current[queueIndexRef.current], evtCtx);
            queueIndexRef.current++;
            lastEventTimeRef.current = now;
          }
        } else if (!allDoneRef.current) {
          allDoneRef.current = true;
          setAllDone(true); setIsPlaying(false);
          return;
        }

        const animDt = dt * speed;
        const cam = cameraRef.current;

        // Camera lerp
        cam.zoom = lerp(cam.zoom, cam.targetZoom, 0.04);
        cam.offsetX = lerp(cam.offsetX, cam.targetOffsetX, 0.04);
        cam.offsetY = lerp(cam.offsetY, cam.targetOffsetY, 0.04);
        if (cam.shakeTimer > 0) {
          cam.shakeTimer -= dt;
          cam.shakeX = (Math.random() - 0.5) * 6;
          cam.shakeY = (Math.random() - 0.5) * 6;
        } else { cam.shakeX = 0; cam.shakeY = 0; }

        // Round transition fade
        const rt = roundTransitionRef.current;
        if (rt.timer > 0) {
          rt.timer -= dt * speed;
          rt.alpha = clamp(rt.timer / 0.6, 0, 1);
        }

        // ── Update sprites ──────────────────────────────────────────────
        const arenaLeft   = ARENA_INSET + CIRCLE_RADIUS + 2;
        const arenaRight  = CANVAS_W - ARENA_INSET - CIRCLE_RADIUS - 2;
        const arenaTop    = ARENA_INSET + CIRCLE_RADIUS + 2;
        const arenaBottom = CANVAS_H - ARENA_INSET - CIRCLE_RADIUS - HP_BAR_H - 4;
        const teamBoundary = CANVAS_W / 2;

        spritesRef.current.forEach(s => {
          if (s.flashTimer > 0) s.flashTimer = Math.max(0, s.flashTimer - dt * 2.5 * speed);
          if (s.shakeX !== 0 || s.shakeY !== 0) {
            s.shakeX *= 0.85; s.shakeY *= 0.85;
            if (Math.abs(s.shakeX) < 0.3) s.shakeX = 0;
            if (Math.abs(s.shakeY) < 0.3) s.shakeY = 0;
          }

          // Breathing
          s.breathPhase += dt * 2.2;
          if (s.auraTimer > 0) s.auraTimer -= dt * speed;
          if (s.comboTimer > 0) { s.comboTimer -= dt * speed; if (s.comboTimer <= 0) s.comboCount = 0; }

          switch (s.state) {
            case 'idle': {
              s.x += s.vx * speed; s.y += s.vy * speed;
              const drift = 0.003 * speed;
              s.vx += (s.baseX - s.x) * drift;
              s.vy += (s.baseY - s.y) * drift;
              s.vx += (Math.random() - 0.5) * 0.025 * speed;
              s.vy += (Math.random() - 0.5) * 0.025 * speed;
              const vMax = IDLE_SPEED * 1.5;
              const vLen = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
              if (vLen > vMax) { s.vx = (s.vx / vLen) * vMax; s.vy = (s.vy / vLen) * vMax; }
              if (s.x < arenaLeft)   { s.x = arenaLeft;   s.vx =  Math.abs(s.vx); }
              if (s.x > arenaRight)  { s.x = arenaRight;  s.vx = -Math.abs(s.vx); }
              if (s.y < arenaTop)    { s.y = arenaTop;     s.vy =  Math.abs(s.vy); }
              if (s.y > arenaBottom) { s.y = arenaBottom;  s.vy = -Math.abs(s.vy); }
              if (s.team === 'teamOne'  && s.x > teamBoundary - CIRCLE_RADIUS) s.vx = -Math.abs(s.vx);
              if (s.team === 'teamTwo'  && s.x < teamBoundary + CIRCLE_RADIUS) s.vx =  Math.abs(s.vx);
              break;
            }
            case 'charging': {
              s.stateProgress = Math.min(1, s.stateProgress + animDt / CHARGE_DURATION);
              const t = s.stateProgress < 0.5
                ? 2 * s.stateProgress * s.stateProgress
                : 1 - Math.pow(-2 * s.stateProgress + 2, 2) / 2;
              s.x = s.stateOriginX + (s.stateTargetX - s.stateOriginX) * t;
              s.y = s.stateOriginY + (s.stateTargetY - s.stateOriginY) * t;
              if (s.stateProgress >= 1) {
                s.stateOriginX = s.x; s.stateOriginY = s.y;
                s.stateTargetX = s.baseX; s.stateTargetY = s.baseY;
                s.state = 'recoiling'; s.stateProgress = 0;
              }
              break;
            }
            case 'recoiling': {
              s.stateProgress = Math.min(1, s.stateProgress + animDt / (CHARGE_DURATION * 0.7));
              s.x = s.stateOriginX + (s.stateTargetX - s.stateOriginX) * s.stateProgress;
              s.y = s.stateOriginY + (s.stateTargetY - s.stateOriginY) * s.stateProgress;
              if (s.stateProgress >= 1) { s.x = s.baseX; s.y = s.baseY; s.state = 'idle'; }
              break;
            }
            case 'dodging': {
              s.stateProgress = Math.min(1, s.stateProgress + animDt / 0.35);
              if (s.stateProgress < 0.5) {
                const p = s.stateProgress / 0.5;
                s.x = s.stateOriginX + (s.stateTargetX - s.stateOriginX) * p;
                s.y = s.stateOriginY + (s.stateTargetY - s.stateOriginY) * p;
              } else {
                const p = (s.stateProgress - 0.5) / 0.5;
                s.x = s.stateTargetX + (s.baseX - s.stateTargetX) * p;
                s.y = s.stateTargetY + (s.baseY - s.stateTargetY) * p;
              }
              if (s.stateProgress >= 1) { s.x = s.baseX; s.y = s.baseY; s.state = 'idle'; }
              break;
            }
            case 'dead': {
              // Gentle death fall, settle at minimum values then stop
              if (s.opacity > 0.3)  s.opacity = Math.max(0.3, s.opacity - dt * 0.6);
              if (s.rotation < Math.PI / 8) s.rotation += dt * 2.0;
              if (s.scale > 0.75)  s.scale = Math.max(0.75, s.scale - dt * 0.2);
              break;
            }
            case 'surrendered':
            case 'collapsed': {
              if (s.scale > 0.9) s.scale = Math.max(0.9, s.scale - dt * 0.1);
              if (s.opacity > 0.6) s.opacity = Math.max(0.6, s.opacity - dt * 0.2);
              break;
            }
            default: break;
          }
        });

        // Particles & floats
        particlesRef.current = updateParticles(particlesRef.current, dt);
        dmgFloatsRef.current = dmgFloatsRef.current
          .map(f => ({ ...f, y: f.y + f.vy * dt, vy: f.vy * 0.88, opacity: f.opacity - dt * 0.85 }))
          .filter(f => f.opacity > 0);

        // ── Render ──────────────────────────────────────────────────────
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

        // World-space (camera-affected)
        ctx.save();
        applyCameraTransform(ctx, cam);
        drawArena(ctx, timeRef.current, themeBorderColorRef.current);
        drawParticles(ctx, particlesRef.current);
        spritesRef.current.forEach(s => drawSprite(ctx, s, imageCacheRef.current, svgCacheRef.current, tintedSvgCacheRef.current, themeAccentRef.current));
        drawDamageFloats(ctx, dmgFloatsRef.current);
        ctx.restore();
        resetCameraTransform(ctx);

        // HUD (stable, not affected by camera shake/zoom)
        drawRoundBadge(ctx, currentRoundNumRef.current);
        drawTeamHPBars(ctx, spritesRef.current);

        // Overlays
        if (rt.alpha > 0) drawRoundTransition(ctx, rt.round, rt.alpha);

      } catch (err) {
        console.error('[BattleViewer2D] render error:', err);
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafIdRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, battleData]);

  useEffect(() => {
    if (logContainerRef.current) logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
  }, [logVersion]);

  const participants = battleData ? Object.entries(battleData.reportData.participantSummaries) : [];
  const teamOneList   = participants.filter(([, p]) => p.team === 'teamOne');
  const teamTwoList   = participants.filter(([, p]) => p.team === 'teamTwo');
  const teamThreeList = participants.filter(([, p]) => p.team === 'teamThree');
  const teamFourList  = participants.filter(([, p]) => p.team === 'teamFour');

  // Extract reward lines from combat log
  const rewardLines = useMemo(() => {
    if (!battleData) return [];
    return battleData.reportData.combatLog
      .map(line => {
        const { eventType, text } = parseCombatTag(line);
        if (isRewardLine(eventType, text)) return text.replace(/@@[^@]+@@/g, '').trim();
        return null;
      })
      .filter((l): l is string => !!l && l.length > 0);
  }, [battleData]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        <span className={styles.titleLine} />
        <span className={styles.titleText}>⚔ 2D Battle Viewer ⚔</span>
        <span className={styles.titleLine} />
      </h2>

      {!initialBattleData && (
        <div className={styles.loadSection}>
          <input
            type="number" className={styles.battleIdInput}
            placeholder="Enter Battle ID" value={battleIdInput}
            onChange={e => setBattleIdInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadBattle()}
          />
          <button className={styles.loadButton} onClick={loadBattle} disabled={loading}>
            {loading ? 'Loading…' : 'Load Battle'}
          </button>
          {errorMsg && <span className={styles.errorMsg}>{errorMsg}</span>}
        </div>
      )}

      {battleData && (
        <div className={styles.viewerLayout}>
          {/* Left side panels */}
          <div className={styles.sidePanel}>
            {teamOneList.length > 0 && (
              <LegendTeam label="Team One" color={TEAM_COLORS.teamOne}
                entries={teamOneList} sprites={spritesRef.current} logVersion={logVersion} myMercenaryId={myMercenaryId} />
            )}
            {teamThreeList.length > 0 && (
              <LegendTeam label="Team Three" color={TEAM_COLORS.teamThree}
                entries={teamThreeList} sprites={spritesRef.current} logVersion={logVersion} myMercenaryId={myMercenaryId} />
            )}
          </div>

          {/* Arena + controls + log */}
          <div className={styles.arenaSection}>
            <div className={styles.canvasWrapper}>
              <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className={styles.canvas}
                onMouseMove={handleCanvasMouseMove} onMouseLeave={handleCanvasMouseLeave} />
              {/* Sprite tooltip */}
              {hoveredSprite && battleData && (() => {
                const p = battleData.reportData.participantSummaries[hoveredSprite.key];
                const sprite = spritesRef.current.get(hoveredSprite.key);
                if (!p) return null;
                const wpn = p.weaponType || sprite?.weaponType || 'Unarmed';
                const off = p.hasShield ? 'Shield'
                  : (p.offHandType || sprite?.offHandType || null);
                return (
                  <div className={styles.spriteTooltip}
                    style={{ left: hoveredSprite.x, top: hoveredSprite.y }}>
                    <div className={styles.ttName}>
                      {p.isBot && <span className={styles.ttBot}>B</span>}
                      {p.name}
                    </div>
                    {p.level != null && <div className={styles.ttLine}>Level {p.level} {p.race ?? ''}</div>}
                    {p.strategy && <div className={styles.ttLine}>Strategy: {p.strategy}</div>}
                    <div className={styles.ttLine}>Main: {wpn}{off ? ` · Off: ${off}` : ''}</div>
                  </div>
                );
              })()}
              {/* Winner overlay — death variant when a participant permanentlyDied */}
              {allDone && battleData.reportData.winner && (() => {
                const winTeam = battleData.reportData.winner;
                const teamColor = TEAM_COLORS[winTeam] ?? '#ffd700';
                const isDraw = winTeam === 'draw';
                const winnerParticipants = isDraw ? []
                  : Object.values(battleData.reportData.participantSummaries).filter(p => p.team === winTeam);
                const fallenParticipant = Object.values(battleData.reportData.participantSummaries).find(p => p.permanentlyDied);
                const deathMercenaryName = fallenParticipant?.name ?? null;

                if (deathMercenaryName) {
                  return (
                    <div className={styles.deathOverlay}>
                      <div className={styles.deathOverlayInner}>
                        <div className={styles.deathOverlaySkulls}>☠</div>
                        <div className={styles.deathOverlayTitle}>{deathMercenaryName} Has Fallen</div>
                        {!isDraw && (
                          <div className={styles.deathOverlayWinner} style={{ color: teamColor }}>
                            {teamDisplayName(winTeam)} Wins
                          </div>
                        )}
                        {winnerParticipants.length > 0 && (
                          <div className={styles.winnerNames}>
                            {winnerParticipants.map((p, i) => (
                              <span key={i}
                                className={`${styles.winnerName} ${p.isLegend ? styles.winnerNameLegend : ''}`}
                                style={{ animationDelay: `${(i * 0.45).toFixed(2)}s`, color: p.isLegend ? '#ffd700' : teamColor }}>
                                {p.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className={styles.winnerOverlay}>
                    <div className={styles.winnerOverlayInner}>
                      <div className={styles.winnerOverlayLabel}>{isDraw ? 'Draw' : 'Victory'}</div>
                      {!isDraw && (
                        <div className={styles.winnerOverlayTeam} style={{ color: teamColor }}>
                          {teamDisplayName(winTeam)}
                        </div>
                      )}
                      {winnerParticipants.length > 0 && (
                        <div className={styles.winnerNames}>
                          {winnerParticipants.map((p, i) => (
                            <span key={i}
                              className={`${styles.winnerName} ${p.isLegend ? styles.winnerNameLegend : ''}`}
                              style={{ animationDelay: `${(i * 0.45).toFixed(2)}s`, color: p.isLegend ? '#ffd700' : teamColor }}>
                              {p.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Battle Summary, shown after battle ends */}
            {allDone && battleData && (
              <BattleSummaryPanel
                summaries={battleData.reportData.participantSummaries}
                rewardLines={rewardLines}
              />
            )}

            {/* Playback controls */}
            <div className={styles.controls}>
              <button className={styles.controlBtn} onClick={() => setIsPlaying(p => !p)}
                disabled={allDone && !isPlaying}>
                {isPlaying ? '⏸ Pause' : '▶ Play'}
              </button>
              <button className={styles.controlBtn} onClick={restart}>↺ Restart</button>
              <button className={styles.controlBtn} onClick={skipRound} disabled={allDone}
                title="Skip to the next round">⏭ Skip Round</button>
              <button className={styles.controlBtn} onClick={skipToEnd} disabled={allDone}
                title="Jump to the end of the battle">⏩ Skip to End</button>
              <div className={styles.speedGroup}>
                <span className={styles.speedLabel}>Speed:</span>
                {([0.5, 1, 2, 4] as const).map(s => (
                  <button key={s}
                    className={`${styles.speedBtn} ${speedMultiplier === s ? styles.speedActive : ''}`}
                    onClick={() => handleSetSpeed(s)}>{s}×</button>
                ))}
              </div>
            </div>

            {/* Combat log */}
            <div className={styles.logWrapper}>
              <div className={styles.logHeader}>Combat Log</div>
              <div ref={logContainerRef} className={styles.logContainer}>
                {pendingEntriesRef.current.map(entry => (
                  <div key={entry.id} className={styles.logEntry}
                    style={{ color: entry.color, fontWeight: entry.bold ? 700 : 400 }}>
                    {entry.text}
                  </div>
                ))}
                {logRoundsRef.current.map((round, idx) => {
                  const isCurrentRound = !allDone && idx === logRoundsRef.current.length - 1;
                  const isExpanded = isCurrentRound || expandedRoundIds.has(round.id);
                  return (
                    <div key={round.id} className={styles.logRoundGroup}>
                      <div className={`${styles.logRoundHeader} ${isCurrentRound ? styles.logRoundCurrent : ''}`}
                        onClick={() => toggleRound(round.id)}>
                        <span className={styles.logRoundToggle}>{isExpanded ? '\u25be' : '\u25b8'}</span>
                        {' '}Round {round.roundNum}
                        {!isExpanded && round.entries.length > 0 && (
                          <span className={styles.logRoundCount}> ({round.entries.length})</span>
                        )}
                      </div>
                      {isExpanded && (
                        <div className={styles.logRoundEntries}>
                          {round.entries.map(entry => (
                            <div key={entry.id} className={styles.logEntry}
                              style={{ color: entry.color, fontWeight: entry.bold ? 700 : 400 }}>
                              {entry.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right side panels */}
          <div className={styles.sidePanel}>
            {teamTwoList.length > 0 && (
              <LegendTeam label="Team Two" color={TEAM_COLORS.teamTwo}
                entries={teamTwoList} sprites={spritesRef.current} logVersion={logVersion} myMercenaryId={myMercenaryId} />
            )}
            {teamFourList.length > 0 && (
              <LegendTeam label="Team Four" color={TEAM_COLORS.teamFour}
                entries={teamFourList} sprites={spritesRef.current} logVersion={logVersion} myMercenaryId={myMercenaryId} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Legend sub-components ───────────────────────────────────────────────────

interface LegendTeamProps {
  label: string; color: string;
  entries: [string, ParticipantSummary][];
  sprites: Map<string, Sprite>;
  logVersion: number;
  myMercenaryId?: number;
}

const LegendTeam: React.FC<LegendTeamProps> = ({ label, color, entries, sprites, myMercenaryId }) => (
  <div className={styles.legendSection} style={{ borderTop: `3px solid ${color}` }}>
    <div className={styles.legendTeamHeader} style={{ color }}>{label}</div>
    {entries.map(([key, p]) => (
      <LegendRow key={key} p={p} sprite={sprites.get(key)} teamColor={color} myMercenaryId={myMercenaryId} />
    ))}
  </div>
);

interface LegendRowProps { p: ParticipantSummary; sprite: Sprite | undefined; teamColor: string; myMercenaryId?: number; }

const LegendRow: React.FC<LegendRowProps> = ({ p, sprite, teamColor, myMercenaryId }) => {
  const isOwner = !myMercenaryId || p.combatantId === myMercenaryId;
  const state = sprite?.state;
  const hp = sprite?.hp ?? p.finalHP;
  const maxHp = sprite?.maxHp ?? p.maxHP;
  const hpFrac = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
  const isDead = state === 'dead' || (!sprite && p.isDead);
  const isSurr = state === 'surrendered' || (!sprite && p.surrendered);
  const isDown = state === 'collapsed' || (!sprite && p.collapsed);
  const isHospitalized = sprite?.hospitalized ?? false;
  const isLegend = sprite?.isLegend ?? p.isLegend ?? false;
  const isBot = p.isBot ?? false;
  const statusSvg = isDead ? 'skull' : isSurr ? 'flag' : (isDown || isHospitalized) ? 'zzz' : null;

  return (
    <div className={styles.legendRow} style={{ opacity: isDead ? 0.45 : 1 }}>
      <div className={styles.legendNameRow}>
        {statusSvg
          ? <img src={`/img/battle_icons/${statusSvg}.svg`} className={styles.legendStatusIcon} alt={statusSvg} />
          : <span className={styles.legendDot} style={{ background: teamColor }} />
        }
        <span className={styles.legendName}
          style={isLegend ? { color: '#ffd700', textShadow: '0 0 6px rgba(255,215,0,0.4)' } : undefined}>
          {isBot && <span className={styles.legendBotBadge}>B</span>}
          {isLegend && <span className={styles.legendCrown}></span>}
          {p.name}
        </span>
      </div>
      {!isDead && (
        <div className={styles.legendHpBar}>
          <div className={styles.legendHpFill} style={{
            width: `${hpFrac * 100}%`,
            background: hpFrac > 0.5 ? '#4caf50' : hpFrac > 0.25 ? '#ff9800' : '#f44336',
          }} />
        </div>
      )}
    </div>
  );
};

// ─── Battle Summary Panel (shown after battle ends) ─────────────────────────

interface BattleSummaryPanelProps {
  summaries: Record<string, ParticipantSummary>;
  rewardLines: string[];
}

const BattleSummaryPanel: React.FC<BattleSummaryPanelProps> = ({ summaries, rewardLines }) => {
  // Group by team
  const teams = new Map<string, [string, ParticipantSummary][]>();
  for (const [key, p] of Object.entries(summaries)) {
    const list = teams.get(p.team) ?? [];
    list.push([key, p]);
    teams.set(p.team, list);
  }

  // Show taunts column only if any participant landed a taunt
  const anyTaunts = Object.values(summaries).some(p => (p.taunts ?? 0) > 0);

  return (
    <div className={styles.summaryPanel}>
      <div className={styles.summaryHeader}>Battle Summary</div>
      <div className={styles.summaryTableWrap}>
        <table className={styles.summaryTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Dmg</th>
              <th>Best</th>
              <th>Taken</th>
              <th>Atk</th>
              <th>Dodge</th>
              <th>Parry</th>
              <th>Block</th>
              <th>Miss</th>
              <th>Crit</th>
              {anyTaunts && <th>Taunt</th>}
            </tr>
          </thead>
          <tbody>
            {[...teams.entries()].map(([team, entries]) => {
              const color = TEAM_COLORS[team] ?? '#888';
              const totals = {
                damage: 0, highest: 0, taken: 0,
                attacks: 0, dodges: 0, parries: 0, blocks: 0, missed: 0, crits: 0, taunts: 0,
              };
              entries.forEach(([, p]) => {
                totals.damage  += p.damage ?? 0;
                totals.highest  = Math.max(totals.highest, p.highestDamageDealt ?? 0);
                totals.taken   += p.damageTaken ?? 0;
                totals.attacks += p.attacksMade ?? 0;
                totals.dodges  += p.dodgedAttacks ?? 0;
                totals.parries += p.parries ?? 0;
                totals.blocks  += p.blockedAttacks ?? 0;
                totals.missed  += p.missedAttacks ?? 0;
                totals.crits   += p.criticalHits ?? 0;
                totals.taunts  += p.taunts ?? 0;
              });
              return (
                <React.Fragment key={team}>
                  {entries.map(([key, p]) => (
                    <tr key={key}>
                      <td style={{ color, fontWeight: 600 }}>{p.name}</td>
                      <td>{p.damage ?? 0}</td>
                      <td>{p.highestDamageDealt ?? 0}</td>
                      <td>{p.damageTaken ?? 0}</td>
                      <td>{p.attacksMade ?? 0}</td>
                      <td>{p.dodgedAttacks ?? 0}</td>
                      <td>{p.parries ?? 0}</td>
                      <td>{p.blockedAttacks ?? 0}</td>
                      <td>{p.missedAttacks ?? 0}</td>
                      <td>{p.criticalHits ?? 0}</td>
                      {anyTaunts && <td>{p.taunts ?? 0}</td>}
                    </tr>
                  ))}
                  {entries.length > 1 && (
                    <tr className={styles.summaryTotalRow}>
                      <td style={{ color }}>Team Total</td>
                      <td>{totals.damage}</td>
                      <td>{totals.highest}</td>
                      <td>{totals.taken}</td>
                      <td>{totals.attacks}</td>
                      <td>{totals.dodges}</td>
                      <td>{totals.parries}</td>
                      <td>{totals.blocks}</td>
                      <td>{totals.missed}</td>
                      <td>{totals.crits}</td>
                      {anyTaunts && <td>{totals.taunts}</td>}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {rewardLines.length > 0 && (
        <div className={styles.rewardsSection}>
          <div className={styles.rewardsHeader}>Battle Rewards</div>
          {rewardLines.map((line, i) => (
            <div key={i} className={styles.rewardLine}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BattleViewer2D;
